import { Spin } from '../Ant';
import SkeletonTransactionList from '../common/skeleton/skeletonTransactionList';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTES, TransactionFilters, UserType } from '../../constants/constants';
import { useEffect, useMemo, useState } from 'react';
import { IHourTransaction, ITransaction } from '../../interface/transaction';
import { useFetchMyLastSevenDaysHourTransactions } from '../../hooks/useFetchMyLastSevenDaysHourTransactions';
import dayjs from 'dayjs';
import { TransactionStatus } from '../../constants/transaction';
import {
  formatDateGroupTitle,
  formatGroupHours,
  groupTransactionsByDate,
} from '../../constants/utilityFunctions';
import { useFetchDashboardTransactions } from '../../hooks/useFetchTransactions';
import RenderTransactionRestrictions from '../dashboard/addTransaction/renderTransactionRestrictions';
import NoData from '../dashboard/noData';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import TransactionCard from '../dashboard/transactionCard';
import { DocumentTextEmpty } from '../icons';
import CalendarView from '../task/calendar';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useScrollToDate } from '../../hooks/useScrollToDate';
import { useSearchParams } from 'react-router-dom';
import { useTransaction } from '../../context/TransactionContext';
import { useCopyTransaction } from '../../hooks/useCopyTransaction';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SortSelector from '../sortPopover/sortSelector';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import CollapsibleDateGroup from '../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../hooks/useCollapsedDateGroups';
import { useFetchAllPages } from '../../hooks/useFetchAllPages';

interface IDayTransactionGroup {
  key: string;
  totalBillable: number;
  transactions: ITransaction[];
}

const Transactions = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { push } = useNavigationStack();
  const [searchParams, setSearchParams] = useSearchParams();
  const transactionFilterOptions = [
    { label: t('transaction:filter.allTransactions'), value: TransactionFilters.all },
    { label: t('transaction:filter.openTransactions'), value: TransactionFilters.open },
  ];
  const initialSearch = searchParams.get('transactionSearch') || '';
  const initialFilterValue = searchParams.get('transactionFilter');
  const initialSelectedDate = searchParams.get('selectedDate');
  const initialStartDate = searchParams.get('startDate');
  const initialEndDate = searchParams.get('endDate');
  const initialFilter = transactionFilterOptions.find((opt) => opt.value === initialFilterValue);
  const { updateTransaction } = useTransaction();
  const hidePricingInfo =
    user?.userRole?.hidePricingInformation === true || user?.userType === UserType.RESTRICTED;
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [selectedFilter, setSelectedFilter] = useState<{ label: string; value: string }>(
    initialFilter ?? {
      label: t('transaction:filter.allTransactions'),
      value: TransactionFilters.all,
    },
  );
  const [copyClicked, setCopyClicked] = useState<boolean>();
  const { data: hourTransactions } = useFetchMyLastSevenDaysHourTransactions();
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const { copyTransaction, startTransactionOnSameMatter } = useCopyTransaction({
    setCopyClicked,
    setSelectedTransaction,
  });
  const [dateRange, setDateRange] = useState({
    endDate: initialStartDate || `${dayjs().startOf('isoWeek').format('YYYY-MM-DD')}`,
    startDate: initialEndDate || `${dayjs().endOf('isoWeek').format('YYYY-MM-DD')}`,
  });
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(
    initialSelectedDate ? dayjs(initialSelectedDate) : dayjs(),
  );
  const activeSort: IActiveSortState = {
    field: 'transactionDate',
    direction: 'DESC',
  };
  const { getDateRef, scrollToDate } = useScrollToDate();
  const transactionSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'transactionDate',
        label: t('transaction:transactionDate'),
        type: 'date',
        defaultDirection: 'DESC',
      },
    ],
    [t],
  );
  const {
    data: transactionList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
    isLoading: isLoadingRecentTransactions,
    isFetching,
  } = useFetchDashboardTransactions({
    startDate: dateRange?.endDate,
    endDate: dateRange?.startDate,
    search: searchValue,
    relations: [
      'matter:matterNumber,subject,currency,invoiceableType,language',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'unit:translations',
      'transactionType',
    ],
    extraFilters: {
      transactionUserId: user?.id ?? '',
      ...(selectedFilter.value.includes(TransactionFilters.open)
        ? { status: TransactionStatus.open }
        : {}),
    },
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    enabled: !!user?.id,
    fields: [
      'billableQuantity',
      'totalPrice',
      'totalPriceInCurrency',
      'unitId',
      'unitPrice',
      'unitPriceInCurrency',
      'status',
      'subject',
      'transactionDate',
      'matterId',
      'workedHours',
      'hasImportantNotes',
      'hasNotes',
      'createdOn',
    ],
  });
  const { isCollapsed, toggle, expand, getToggleAllControl } = useCollapsedDateGroups(
    DateGroupList.transactions,
  );
  useEffect(() => {
    setSearchParams({
      transactionSearch: searchValue,
      transactionFilter: selectedFilter?.value || '',
      selectedDate: selectedDate.format('YYYY-MM-DD'),
      startDate: dateRange.endDate,
      endDate: dateRange.startDate,
    });
  }, [searchValue, selectedFilter, selectedDate, dateRange]);

  const groupedTransactions = useMemo((): IDayTransactionGroup[] => {
    if (transactionList && transactionList.length > 0) {
      const sortedTransactions = [...transactionList].sort((a, b) =>
        dayjs(b.createdOn).diff(dayjs(a.createdOn)),
      );
      const billableHoursData = hourTransactions ?? [];
      const billableHoursMap = billableHoursData.reduce(
        (acc: Record<string, number>, entry: IHourTransaction) => {
          acc[entry.transactionDate] = entry.billableHours;
          return acc;
        },
        {} as Record<string, string>,
      );

      const grouped = sortedTransactions.reduce(
        (acc, transaction) => {
          const key = toDateGroupKey(transaction?.transactionDate);
          acc[key] ??= {
            key,
            totalBillable: billableHoursMap[transaction.transactionDate] ?? 0,
            transactions: [],
          };
          acc[key].transactions.push(transaction);
          return acc;
        },
        {} as Record<string, IDayTransactionGroup>,
      );

      const todayKey = toDateGroupKey(dayjs());
      return (Object.values(grouped) as IDayTransactionGroup[]).sort((a, b) => {
        if (a.key === todayKey) return -1; // Keep "Today" always at the top
        if (b.key === todayKey) return 1;
        return b.key.localeCompare(a.key);
      });
    }
    return [];
  }, [transactionList, hourTransactions]);
  useFetchAllPages({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage });
  const handleAdd = () => {
    updateTransaction({
      transactionDate: selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : undefined,
    } as ITransaction);
    push(ROUTES.ADD_EDIT_TRANSACTION);
  };
  const handleSelectTransaction = (transaction?: ITransaction) => {
    if (transaction && !copyClicked) {
      push(`${ROUTES.ADD_EDIT_TRANSACTION}/${transaction.id}`);
    } else if (transaction && copyClicked) {
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      setCopyClicked(false);
      updateTransaction(null);
      setSelectedTransaction(undefined);
    }
  };
  const handleCopyTransaction = (transaction: ITransaction) => {
    copyTransaction(transaction);
  };
  const renderLoader = () => {
    if (isFetching) {
      return <Spin />;
    }
    return null;
  };

  const groupedByDate = groupTransactionsByDate(transactionList, t);

  return (
    <CollapsibleListLayout
      testId="transactions-listing"
      contentGap="gutter"
      contentClassName="items-center w-full"
      pinnedHeader={
        <CalendarView
          onWeekChange={(start, end) => {
            setDateRange({
              endDate: `${dayjs(start).startOf('day').format('YYYY-MM-DD')}`,
              startDate: `${dayjs(end).endOf('day').format('YYYY-MM-DD')}`,
            });
          }}
          onDateChange={(dateObj) => {
            const dateKey = toDateGroupKey(dateObj);
            expand(dateKey);
            scrollToDate(dateKey);
          }}
          onSelectedDateChange={(date) => {
            setSelectedDate(dayjs(date));
          }}
          groupedMap={groupedByDate}
          initialWeekStart={initialStartDate ? new Date(initialStartDate) : undefined}
          customDate={initialSelectedDate ? new Date(initialSelectedDate) : undefined}
        />
      }
      header={
        <SearchInputWithFilters
          dateGroupToggle={getToggleAllControl(groupedTransactions.map((group) => group.key))}
          handleAdd={handleAdd}
          tooltipTextForAdd={t('transaction:addTransaction')}
          searchTitle={t('transaction:search')}
          searchValue={searchValue}
          filterTitle={t('transaction:filter.label')}
          data-testid="top-bar"
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['transactions'] });
            return refetch();
          }}
          radioOptions={transactionFilterOptions}
          selectedFilter={[selectedFilter]}
          setSelectedFilter={(val) => setSelectedFilter(val)}
          clearFilter={() => {
            setSelectedFilter({
              label: t('transaction:filter.allTransactions'),
              value: TransactionFilters.all,
            });
          }}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={transactionSortOptions}
            activeSort={activeSort}
            disabled
          />
        </SearchInputWithFilters>
      }
    >
      {groupedTransactions.length > 0 && (
        <>
          {groupedTransactions.map((group) => (
            <CollapsibleDateGroup
              key={group.key}
              ref={getDateRef(group.key)}
              className="scroll-anchor"
              title={formatDateGroupTitle(group.key, t)}
              itemCount={t('transaction:transactionCount', { count: group.transactions.length })}
              total={formatGroupHours(group.totalBillable, t)}
              collapsed={isCollapsed(group.key)}
              onToggle={() => toggle(group.key)}
            >
              {group.transactions.map((transaction) => (
                <TransactionCard
                  data-testid="transaction-card"
                  key={transaction.id}
                  transaction={transaction}
                  hidden={hidePricingInfo}
                  onEdit={(transaction) => {
                    setSelectedTransaction(transaction);
                  }}
                  onCopy={() => {
                    handleCopyTransaction(transaction);
                  }}
                  onNewToSameMatter={startTransactionOnSameMatter}
                />
              ))}
            </CollapsibleDateGroup>
          ))}
        </>
      )}
      {groupedTransactions.length === 0 && !isLoadingRecentTransactions && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData header={t('transaction:noTransactionForThisWeek')} icon={<DocumentTextEmpty />} />
        </div>
      )}
      {isLoadingRecentTransactions ? <SkeletonTransactionList /> : renderLoader()}
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleSelectTransaction}
      />
    </CollapsibleListLayout>
  );
};

export default Transactions;
